import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { flexRender, Header } from '@tanstack/react-table';
import { TableHead } from '@ui/components';
import { cn } from '@ui/lib/utils';
import { CSSProperties, useMemo } from 'react';
import { useAppTableContext } from '../../lib/context/use-app-table-context';
import { getTableHeaderStyles } from '../../lib/utils/get-table-header-styles';
import { AppTableResize } from '../app-table-resize';

interface DataTableDragHeaderProps<TData, TValue> {
  header: Header<TData, TValue>;
  style?: CSSProperties;
  className?: string;
}

export const AppTableHeader = <TData, TValue>(
  props: DataTableDragHeaderProps<TData, TValue>,
) => {
  const { header, style, className } = props;

  const { isResizable, isDraggable } = useAppTableContext();

  const isServiceColumn =
    header.column.id === 'select' || header.column.id === 'actions';

  const _isDraggable = useMemo(
    () => !isServiceColumn && isDraggable,
    [isServiceColumn, isDraggable],
  );

  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id: header.column.id,
      disabled: !_isDraggable,
    });

  const transformStyle: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition: 'width transform 0.2s ease-in-out',
  };

  const styles = useMemo(
    () => ({
      ...getTableHeaderStyles({
        column: header.column,
        isDragging,
      }),
      ...transformStyle,
      ...(isResizable && {
        width: header.getSize(),
      }),
      ...(isServiceColumn && {
        width: 40,
      }),
      ...style,
    }),
    [
      header.column,
      header.getSize(),
      isDragging,
      isServiceColumn,
      isResizable,
      transformStyle,
      style,
    ],
  );

  return (
    <TableHead
      key={header.id}
      ref={setNodeRef}
      className={cn(
        'group relative h-8 m-0 p-0 text-xs font-bold [&:not(:last-child)]:border-r',
        _isDraggable && 'cursor-move',
        className,
      )}
      colSpan={header.colSpan}
      style={styles}
      {...attributes}
      {...listeners}
    >
      <div className="flex size-full items-center justify-center">
        {header.isPlaceholder
          ? null
          : flexRender(header.column.columnDef.header, header.getContext())}
      </div>

      {!isServiceColumn && <AppTableResize header={header} />}
    </TableHead>
  );
};

interface AppTableDragHeaderFilterProps<TData, TValue> {
  header: Header<TData, TValue>;
  style?: CSSProperties;
}

export const AppTableDragHeaderFilter = <TData, TValue>(
  props: AppTableDragHeaderFilterProps<TData, TValue>,
) => {
  const { header, style } = props;

  const { isDraggable, isResizable } = useAppTableContext();

  const isCanFilter = header.column.getCanFilter();

  const { setNodeRef, transform } = useSortable({
    id: header.column.id,
    disabled:
      header.column.id === 'select' ||
      header.column.id === 'actions' ||
      !isDraggable,
  });

  const transformStyle: CSSProperties = {
    transform: CSS.Translate.toString(transform), // translate instead of transform to avoid squishing
    transition: 'width transform 0.2s ease-in-out',
    whiteSpace: 'nowrap',
  };

  const isServiceColumn =
    header.column.id === 'select' || header.column.id === 'actions';

  const styles = useMemo(
    () => ({
      ...getTableHeaderStyles({
        column: header.column,
        isDragging: isDraggable,
      }),
      ...transformStyle,
      ...(isResizable && {
        width: header.getSize(),
      }),
      ...(isServiceColumn && {
        width: 50,
      }),
      ...style,
    }),
    [header.column, header.getSize(), isDraggable, isResizable, style],
  );

  return (
    <TableHead
      key={header.id}
      ref={setNodeRef}
      className="h-7 w-full p-0 [&:not(:last-child)]:border-r"
      style={styles}
    >
      {isCanFilter && (
        <input
          className="size-full px-2 outline-0"
          placeholder="Filter..."
          value={String(header.column?.getFilterValue() ?? '')}
          onChange={(event) =>
            header.column?.setFilterValue(event.target.value)
          }
        />
      )}
    </TableHead>
  );
};
